<script setup lang="ts">
import { useUnderwritingStore } from '@/stores/underwriting';
import { computed, ref, onMounted, nextTick } from 'vue';
import { usePortfolioStore } from '@/stores/portfolio';
import { useGlobalStore } from '@/stores/global';
import { useActuarialStore } from '@/stores/actuarial';
import { api } from '@/services/api';
import { ElMessage } from 'element-plus';
import openErrorMsg from '@/components/openErrorMsg';
import CheckboxList from '@/components/CheckboxListComponent.vue';
import DialogComponent from '@/components/Dialog/DialogComponent.vue';
import CheckboxComponent from '@/components/CheckboxComponent.vue';

const underwritingStore = useUnderwritingStore();
const portfolioStore = usePortfolioStore();
const globalStore = useGlobalStore();
const hierarchyOrderData = ref(underwritingStore.hierarchyOrder);
const parameters = computed(() => portfolioStore.parameters);
const defaultSelection = ref(false);
const isDisabled = ref(false);

onMounted(() => {
  setDefaultCheckedKeys();
  nextTick(() => {
    defaultSelection.value = getDefaultSelection();
    isDisabled.value = getDefaultSelection();
  });
});

function setDefaultCheckedKeys() {
  if (parameters.value['default_hierarchies'].length != 0) {
    for (const x of hierarchies.value) {
      if (hierarchyOrderData.value.includes(x.id)) {
        x.is_checked = true;
      } else {
        x.is_checked = false;
      }
    }
  }
}

function openSuccessMsg(msg: string) {
  ElMessage({
    message: msg,
    type: 'success',
  });
}

const arraysAreEqual = (array1, array2) =>
  array1.length === array2.length && array1.every((value, index) => value === array2[index]);

const actuarialStore = useActuarialStore();

async function setDefault() {
  let parametersCopy = structuredClone(portfolioStore.parameters);
  const checkedKeys = getAllCheckedKeys(true);

  let convertedHierarchies: string[] = [];

  if (getAllCheckedKeys(true).length != parameters.value['hierarchies'].length) {
    for (const i of checkedKeys) {
      convertedHierarchies.push(parameters.value['hierarchies'][i]);
    }
  }

  if (!arraysAreEqual(portfolioStore.parameters['default_hierarchies'], convertedHierarchies)) {
    parametersCopy['default_hierarchies'] = convertedHierarchies;
    globalStore.setLoading(true);
    await api
      .post(import.meta.env.VITE_API_URL + 'portfolio/save-parameters', {
        bounce_id: portfolioStore.selectedBounceID,
        new_parameters: parametersCopy,
      })
      .then((res) => {
        actuarialStore.createDraft(res);
        globalStore.setLoading(false);
        openSuccessMsg('Successfully set new default hierarchies.');
      })
      .catch((e) => {
        console.log(e);
        globalStore.setLoading(false);
        openErrorMsg('Error!');
      });
  }
}

function onResetDefaultSelection() {
  defaultSelection.value = getDefaultSelection();
  isDisabled.value = getDefaultSelection();
}

function onCheckDefaltSelection(e) {
  defaultSelection.value = e;
  isDisabled.value = false;
}

const getAllCheckedKeys = (check: boolean): number[] => {
  const keys: number[] = [];
  hierarchies.value.forEach((item) => {
    if (item.is_checked == check) {
      keys.push(item.id);
    }
  });
  return keys;
};

function getDefaultSelection() {
  if (
    parameters.value['default_hierarchies'].length == 0 &&
    getAllCheckedKeys(true).length == parameters.value['hierarchies'].length
  ) {
    return true;
  }

  if (parameters.value['default_hierarchies'].length != 0) {
    const checkedKeys = getAllCheckedKeys(true);

    let convertedHierarchiesKey: number[] = [];

    for (const i of parameters.value['default_hierarchies']) {
      convertedHierarchiesKey.push(parameters.value['hierarchies'].findIndex((e: string) => e == i));
    }
    return arraysAreEqual(convertedHierarchiesKey, checkedKeys);
  }
  return false;
}

const hierarchies = ref(portfolioStore.parameters['hierarchies'].map((x, i) => ({ name: x, id: i, is_checked: true })));

function onCloseHierarchyOrderModal() {
  underwritingStore.updateChangeHierarchyOrderModal(false);
}

async function onClickSubmitHierarchy() {
  if (getAllCheckedKeys(true).length == 0) {
    openErrorMsg('Please select at least one hierarchy');
    return;
  }
  if (defaultSelection.value) {
    await setDefault();
  }
  underwritingStore.updateHierarchyOrder(getAllCheckedKeys(true)).then(() => openSuccessMsg('Updated'));
  await underwritingStore.updateChangeHierarchyOrderModal(false);
}
</script>

<template>
  <DialogComponent
    v-model="underwritingStore.isChangeHierarchyOrderModal"
    title="Hierarchy Selection"
    dialog-class="w-1/3 h-1/2 min-w-fit min-h-fit"
  >
    <div>
      <div
        class="custom-scroll h-full border-2 mt-4 border-gray-100 rounded-sm overflow-scroll bg-white"
        data-testid="hierarchy-order"
        style="height: 265px"
      >
        <CheckboxList
          class="overflow-y-scroll border-2 border-gray-100"
          style="height: 305px"
          :data="hierarchies"
          @update:checkbox-list="onResetDefaultSelection"
        />
      </div>
      <CheckboxComponent
        :model-value="defaultSelection"
        :disabled="isDisabled"
        text="Set as default"
        class="mt-3"
        @update:model-value="onCheckDefaltSelection"
      />
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="onCloseHierarchyOrderModal">Cancel</el-button>
        <el-button type="primary" data-testid="submit-hierarchy" @click="onClickSubmitHierarchy">Confirm</el-button>
      </span>
    </template>
  </DialogComponent>
</template>

<style scoped>
.shadow-internal {
  box-shadow: inset 0 0 10px #dddddd;
  background-color: rgb(250, 255, 255);
}

.custom-scroll::-webkit-scrollbar {
  width: 7px;
  height: 0px;
}

.custom-scroll::-webkit-scrollbar-track {
  background: #eeeeee;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: #888;
}

.custom-scroll::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
